import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LeagueInput as LeagueInputType } from '../services/fantasyApi';
import { LogIn, ArrowLeft, Check } from 'lucide-react';
import { theme } from '../styles/theme';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  padding-top: 60px;
  
  @media (max-width: 768px) {
    padding-top: 50px;
  }
`;

const Container = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  font-family: ${theme.typography.fontFamily};
  
  @media (max-width: 768px) {
    margin: 0 20px;
    max-width: calc(100% - 40px);
    padding: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  position: relative;
`;

const BackButton = styled.button`
  background: ${theme.colors.glass.backgroundStrong};
  border: 1px solid ${theme.colors.glass.borderStrong};
  color: ${theme.colors.text.primary};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  backdrop-filter: blur(16px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
    box-shadow: ${theme.shadows.sm};
  }
`;

const TitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
`;

const Title = styled.h3`
  margin: 0;
  color: white;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 20px 0;
  line-height: 1.5;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const Button = styled.button`
  background: rgba(99, 102, 241, 0.9);
  backdrop-filter: blur(20px);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  width: 100%;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
    border-color: rgba(79, 70, 229, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  }
  
  &:disabled {
    background: rgba(209, 213, 219, 0.6);
    color: rgba(107, 114, 128, 0.8);
    border-color: rgba(209, 213, 219, 0.3);
    cursor: not-allowed;
    transform: none;
    opacity: 0.6;
  }
`;

const LeagueGrid = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
  max-height: 300px;
  overflow-y: auto;
`;

const LeagueCard = styled.div<{ $selected?: boolean }>`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  backdrop-filter: blur(16px);
  position: relative;
  box-shadow: ${props => props.$selected ? '0 8px 32px rgba(99, 102, 241, 0.15)' : '0 4px 16px rgba(0, 0, 0, 0.1)'};
  
  &:hover {
    border-color: ${props => props.$selected ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.3)'};
    background: ${props => props.$selected ? 'rgba(99, 102, 241, 0.25)' : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$selected ? '0 12px 40px rgba(99, 102, 241, 0.2)' : '0 8px 24px rgba(0, 0, 0, 0.15)'};
  }
`;

const LeagueName = styled.div`
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const LeagueMeta = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  gap: 12px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
`;

const LogoutButton = styled.button`
  background: rgba(252, 165, 165, 0.3);
  color: #991b1b;
  border: 1px solid rgba(239, 68, 68, 0.4);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(220, 53, 69, 0.3);
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  backdrop-filter: blur(5px);
  background: ${props => 
    props.type === 'success' ? 'rgba(40, 167, 69, 0.1)' :
    props.type === 'error' ? 'rgba(220, 53, 69, 0.1)' :
    'rgba(23, 162, 184, 0.1)'
  };
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid ${props =>
    props.type === 'success' ? 'rgba(40, 167, 69, 0.3)' :
    props.type === 'error' ? 'rgba(220, 53, 69, 0.3)' :
    'rgba(23, 162, 184, 0.3)'
  };
`;

interface YahooLeague {
  league_key: string;
  league_id: string;
  name: string;
  season: string;
  game_code: string;
  num_teams: number;
}

interface Props {
  onLeagueSelect: (input: LeagueInputType) => void;
  onBack?: () => void;
}

export const YahooLogin: React.FC<Props> = ({ onLeagueSelect, onBack }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [leagues, setLeagues] = useState<YahooLeague[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<YahooLeague | null>(null);

  useEffect(() => {
    // Check URL params for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const token = urlParams.get('access_token');
    const leaguesParam = urlParams.get('leagues');

    if (success && token && leaguesParam) {
      try {
        const parsedLeagues = JSON.parse(leaguesParam);
        setAccessToken(token);
        setLeagues(parsedLeagues);
        setIsLoggedIn(true);
        setStatus({ type: 'success', message: `Found ${parsedLeagues.length} Yahoo leagues from 2025 season!` });
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        setStatus({ type: 'error', message: 'Failed to parse league data' });
      }
    } else if (error) {
      setStatus({ type: 'error', message: `Login failed: ${error}` });
    }

    // Check if already logged in (stored in localStorage)
    const storedToken = localStorage.getItem('yahoo_access_token');
    const storedLeagues = localStorage.getItem('yahoo_leagues');
    
    if (storedToken && storedLeagues) {
      try {
        setAccessToken(storedToken);
        setLeagues(JSON.parse(storedLeagues));
        setIsLoggedIn(true);
      } catch (err) {
        // Clear invalid stored data
        localStorage.removeItem('yahoo_access_token');
        localStorage.removeItem('yahoo_leagues');
      }
    }
  }, []);

  const handleLogin = () => {
    setLoading(true);
    setStatus({ type: 'info', message: 'Redirecting to Yahoo login...' });
    
    // Use relative path for Vercel API routes
    window.location.href = '/api/auth/yahoo';
  };

  const handleLeagueClick = (league: YahooLeague) => {
    setSelectedLeague(league);
  };

  const handleNext = () => {
    if (!selectedLeague) return;
    
    // Store token and leagues for future use
    if (accessToken) {
      localStorage.setItem('yahoo_access_token', accessToken);
      localStorage.setItem('yahoo_leagues', JSON.stringify(leagues));
    }

    onLeagueSelect({
      platform: 'yahoo',
      leagueId: selectedLeague.league_key,
      season: parseInt(selectedLeague.season),
      accessToken: accessToken || undefined
    });
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLeagues([]);
    setAccessToken(null);
    setStatus(null);
    
    // Clear stored data
    localStorage.removeItem('yahoo_access_token');
    localStorage.removeItem('yahoo_leagues');
  };

  return (
          <Wrapper>
        <Container>
        <Header>
          {onBack && (
            <BackButton onClick={onBack} aria-label="Go back">
              <ArrowLeft size={20} />
            </BackButton>
          )}
          <TitleContent>
            <Title>Yahoo Fantasy</Title>
            {isLoggedIn && (
              <LogoutButton onClick={handleLogout}>
                Logout
              </LogoutButton>
            )}
          </TitleContent>
          <div style={{ width: '40px' }} /> {/* Spacer for centering */}
        </Header>

      {status && (
        <StatusMessage type={status.type}>
          {status.message}
        </StatusMessage>
      )}

      {!isLoggedIn ? (
        <>
          <Description>
            Login with your Yahoo account to automatically load all your 2025 fantasy leagues. 
            No more copying League IDs or access tokens!
          </Description>
          
          <Button onClick={handleLogin} disabled={loading}>
            {!loading && <LogIn size={18} />}
            {loading ? 'Loading...' : 'Login with Yahoo'}
          </Button>
          
          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
            <Check size={14} style={{display: 'inline', marginRight: '4px'}} />
            Secure OAuth login • 
            <Check size={14} style={{display: 'inline', margin: '0 4px'}} />
            Auto-detects all leagues • 
            <Check size={14} style={{display: 'inline', margin: '0 4px'}} />
            One-click access
          </div>
        </>
      ) : (
        <>
          <Description>
            Successfully logged in! Choose a 2025 league to view:
          </Description>

          <LeagueGrid>
            {leagues.length > 0 ? (
              leagues
                .filter(league => league.game_code === 'nfl') // Only NFL leagues
                .map(league => (
                  <LeagueCard 
                    key={league.league_key} 
                    onClick={() => handleLeagueClick(league)}
                    $selected={selectedLeague?.league_key === league.league_key}
                  >
                    <LeagueName>{league.name}</LeagueName>
                    <LeagueMeta>
                      <span>{league.season} Season</span>
                      <span>{league.num_teams} Teams</span>
                      <span>ID: {league.league_id}</span>
                    </LeagueMeta>
                    {selectedLeague?.league_key === league.league_key && (
                      <Check size={20} style={{ position: 'absolute', top: '12px', right: '12px', color: 'white' }} />
                    )}
                  </LeagueCard>
                ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
                No 2025 NFL fantasy leagues found for your account.
              </div>
            )}
          </LeagueGrid>
          {selectedLeague && (
            <Button onClick={handleNext} style={{ marginTop: '20px' }}>
              Next
            </Button>
          )}
        </>
      )}
      </Container>
    </Wrapper>
  );
};